import { FusionLayout } from "@/parts/fusion-layout";
import { observer } from "mobx-react";
import { useNavigate } from "react-router";
import { store } from "../../store";
import { useCallback, useEffect, useState } from "react";
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationPrevious,
    PaginationNext,
} from "@/components/ui/pagination";
import { cn, useFetchAction } from "@/lib/utils";
import { Search, Filter, Plus, ChevronDown, Download, Sheet, FileSpreadsheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { RobotList } from "../../robots/parts/robot-list";
import { RobotListEmptyState } from "../../robots/parts/robot-list-empty-state";
import {
    RobotFilterPopover,
    RobotFilterTags,
    robotsFilterFromSelection,
    type RobotFilterId,
} from "../../robots/parts/robot-filter-popover";
import { RobotFormDialog, type RobotForm } from "../../robots/parts/robot-form-dialog";
import type { Robot, RobotSortKey } from "../../robots/types";
import { SortDirection } from "../../api/models";
import { toast } from "sonner";
import { createUuid } from "../../utils/uuid";
import { defaultPageSize } from "../../utils/constants";
import { usePageParam } from "@/lib/hooks/page-param";

export const PageRobots = observer((): JSX.Element => {
    const t = store.i18n.t;

    const [page, setPage] = usePageParam();
    const [sortColumn, setSortColumn] = useState<RobotSortKey | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.ASCENDING);
    const [robotDialogOpen, setRobotDialogOpen] = useState(false);
    const [editedRobot, setEditedRobot] = useState<Robot>();
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState<Set<RobotFilterId>>(new Set());

    const fetchRobotsWithQuery = useCallback(() => {
        return store.serviceRobots.fetchRobots({
            page,
            pageSize: defaultPageSize,
            sortKey: sortColumn ?? undefined,
            sortDirection,
            search: search === "" ? undefined : search,
            ...robotsFilterFromSelection(filter),
        });
    }, [page, sortColumn, sortDirection, search, filter]);
    const [data, , , refresh] = useFetchAction(fetchRobotsWithQuery);

    useEffect(() => {
        const interval = setInterval(() => {
            refresh();
        }, 3000);

        return () => clearInterval(interval);
    }, [refresh]);

    const onSearchChange = (value: string): void => {
        setSearch(value);
        setPage(0);
    };

    const onFilterChange = (selected: Set<RobotFilterId>): void => {
        setFilter(selected);
        setPage(0);
    };

    const saveRobot = (form: RobotForm): void => {
        if (editedRobot) {
            store.serviceRobots.updateRobot(editedRobot.id, form);
            toast.success(t("page.robots.toast.updated", { name: form.name }));
        } else {
            store.serviceRobots.addRobot({
                ...form,
                id: createUuid(),
                connectivity: "offline",
                alarms: 0,
                health: "unknown",
            });
            toast.success(t("page.robots.toast.added", { name: form.name }));
        }
        refresh();
    };

    const onSort = (column: RobotSortKey): void => {
        if (sortColumn !== column) {
            setSortColumn(column);
            setSortDirection(SortDirection.ASCENDING);
        } else if (sortDirection === SortDirection.ASCENDING) {
            setSortDirection(SortDirection.DESCENDING);
        } else {
            setSortColumn(null);
        }
        setPage(0);
    };

    // Feature flag redirect
    const navigate = useNavigate();

    useEffect(() => {
        if (!store.serviceRobots.enabled) {
            navigate("/");
        }
    }, [navigate]);

    if (!store.serviceRobots.enabled) {
        return <></>;
    }

    return (
        <FusionLayout breadcrumb={t("page.robots.title")}>
            <div className="border-border flex h-full flex-col overflow-auto rounded-lg border">
                <div className="flex flex-wrap justify-between gap-2 px-4 py-4">
                    <div className="flex flex-1 gap-2">
                        <div className="w-full max-w-[400px]">
                            <Input
                                startIcon={Search}
                                placeholder={t("page.robots.search")}
                                value={search}
                                onChange={(event) => onSearchChange(event.target.value)}
                            />
                        </div>
                        <RobotFilterPopover selected={filter} onChange={onFilterChange}>
                            <Button variant="outline">
                                <Filter />
                                {t("page.robots.filter")}
                            </Button>
                        </RobotFilterPopover>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            onClick={() => {
                                setEditedRobot(undefined);
                                setRobotDialogOpen(true);
                            }}
                        >
                            <Plus size={16} className="mr-2" />
                            {t("page.robots.addRobot")}
                        </Button>
                        <RobotFormDialog
                            open={robotDialogOpen}
                            onOpenChange={setRobotDialogOpen}
                            robot={editedRobot}
                            onSave={saveRobot}
                        />
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline">
                                    {t("page.robots.sortBy")}
                                    <ChevronDown size={16} />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                                <DropdownMenuRadioGroup
                                    unselectable="on"
                                    value={sortColumn ?? ""}
                                    onValueChange={(value) => {
                                        // Unselect if currently active
                                        if (sortColumn === value) {
                                            setSortColumn(null);
                                            setSortDirection(SortDirection.ASCENDING);
                                            setPage(0);
                                        } else {
                                            setSortColumn(value as RobotSortKey);
                                            setSortDirection(SortDirection.ASCENDING);
                                            setPage(0);
                                        }
                                    }}
                                >
                                    <DropdownMenuRadioItem value="model">
                                        {t("page.robots.details.model")}
                                    </DropdownMenuRadioItem>
                                    <DropdownMenuRadioItem value="manufacturer">
                                        {t("page.robots.details.manufacturer")}
                                    </DropdownMenuRadioItem>
                                    <DropdownMenuRadioItem value="department">
                                        {t("page.robots.details.department")}
                                    </DropdownMenuRadioItem>
                                    <DropdownMenuRadioItem value="lifecycle">
                                        {t("page.robots.details.lifecycleState")}
                                    </DropdownMenuRadioItem>
                                    <DropdownMenuRadioItem value="lastSeen">
                                        {t("page.robots.details.lastSeen")}
                                    </DropdownMenuRadioItem>
                                    <DropdownMenuRadioItem value="health">
                                        {t("page.robots.details.health")}
                                    </DropdownMenuRadioItem>
                                    <DropdownMenuRadioItem value="availability">
                                        {t("page.robots.details.availability")}
                                    </DropdownMenuRadioItem>
                                </DropdownMenuRadioGroup>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild disabled>
                                <Button variant="outline">
                                    <Download size={16} />
                                    <ChevronDown size={16} />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                                <DropdownMenuItem>
                                    <Sheet />
                                    Excel
                                </DropdownMenuItem>
                                <DropdownMenuItem>
                                    <FileSpreadsheet />
                                    CSV
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
                <RobotFilterTags
                    selected={filter}
                    onChange={onFilterChange}
                    className="px-4 pb-4"
                />
                {data && data.robots.length === 0 ? (
                    <RobotListEmptyState
                        hasRobots={store.serviceRobots.robots.length > 0}
                        onAddRobot={() => {
                            setEditedRobot(undefined);
                            setRobotDialogOpen(true);
                        }}
                        className="min-h-[420px] border-none"
                    />
                ) : (
                    <>
                        <div className="overflow-auto">
                            <RobotList
                                robots={data?.robots ?? []}
                                sortColumn={sortColumn}
                                sortDirection={sortDirection}
                                onSort={onSort}
                                onEdit={(robot) => {
                                    setEditedRobot(robot);
                                    setRobotDialogOpen(true);
                                }}
                                onDelete={refresh}
                            />
                        </div>
                        <Pagination className="border-border mt-auto justify-end border-t p-2">
                            <PaginationContent>
                                <PaginationItem>
                                    <PaginationPrevious
                                        className={cn(
                                            "cursor-pointer",
                                            page <= 0
                                                ? "pointer-events-none opacity-50"
                                                : undefined,
                                        )}
                                        label={t("component.pagination.previous")}
                                        onClick={() => setPage(page - 1)}
                                    />
                                </PaginationItem>
                                <PaginationItem className="font-medium">{`${page + 1} / ${data?.pageInfo.totalPages ?? 1}`}</PaginationItem>
                                <PaginationItem>
                                    <PaginationNext
                                        className={cn(
                                            "cursor-pointer",
                                            page >= (data?.pageInfo.totalPages ?? 1) - 1
                                                ? "pointer-events-none opacity-50"
                                                : undefined,
                                        )}
                                        label={t("component.pagination.next")}
                                        onClick={() => setPage(page + 1)}
                                    />
                                </PaginationItem>
                            </PaginationContent>
                        </Pagination>
                    </>
                )}
            </div>
        </FusionLayout>
    );
});
